import { dirname } from 'node:path';
import { appendFileSync, mkdirSync } from 'node:fs';
import React, { createContext, useContext, useCallback } from 'react';
import type { FC, ReactNode } from 'react';
import VError from 'verror';
import { extractHttpError, getHttpErrorInfo } from '@wix/cli-http-client';

export function writeCrashReport(
  reportPath: string,
  error: unknown,
  occurredAt: Date
) {
  try {
    let content = `----- CLI crash report at ${occurredAt.toISOString()}\n`;
    if (error instanceof Error) {
      content += `Error Trace\n`;
      content += `${VError.fullStack(error)}\n`;
      content += `Error Info\n`;
      content += `${JSON.stringify(VError.info(error), null, 2)}\n`;

      const httpError = extractHttpError(error);
      if (httpError) {
        content += `Request Info\n`;
        content += `${JSON.stringify(getHttpErrorInfo(httpError), null, 2)}\n`;
      }
    }
    mkdirSync(dirname(reportPath), { recursive: true });
    appendFileSync(reportPath, content);
  } catch {
    // well there is so much we can do...
  }
}

export type WriteDebugLogFn = (error: unknown) => void;

const DebugLogContext = createContext<WriteDebugLogFn | null>(null);

interface Props {
  filePath: string;
  children: ReactNode;
}

export const DebugLogProvider: FC<Props> = ({ filePath, children }) => {
  const writeToDebugLog = useCallback<WriteDebugLogFn>(
    (error) => writeCrashReport(filePath, error, new Date()),
    [filePath]
  );

  return (
    <DebugLogContext.Provider value={writeToDebugLog}>
      {children}
    </DebugLogContext.Provider>
  );
};

export const useDebugLog = (): WriteDebugLogFn => {
  const context = useContext(DebugLogContext);
  if (context == null) {
    throw new Error(
      'useDebugLog() cannot be used without an <DebugLogProvider>'
    );
  }
  return context;
};
